import { useEffect, useState } from "react";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";

const cache = {};

// Dev-only handle, because the deduplication this module exists for is
// otherwise invisible: nothing in the UI distinguishes one shared listener from
// several. Two components observing the same path should show as one entry with
// two listeners, not two entries.
//
//   Object.keys(window.__cache)
//   window.__cache["profile/<id>"].listeners.length
//
// The typeof guard is for the test runner, which has no window.
if (import.meta.env.DEV && typeof window !== "undefined") {
    window.__cache = cache;
}

const subscribeToDocument = (path, listener) => {
    const ref = doc(getFirestore(), path);

    if (!ref) return;

    const unsubscribe = onSnapshot(ref, (docRef) => {
        updateDocument(path, docRef);
    }, (error) => {
        console.error(`Cache document update error on ${path}:\n${error}`)
        updateDocument(path, undefined);
    });

    cache[path] = {
        value: undefined,
        listeners: [listener],
        unsubscribe: unsubscribe
    };
}

// A callback here is a setState, and a snapshot arrives outside React's event
// system, so in React 16 it re-renders synchronously rather than being batched.
// That render can unmount a component, and its cleanup calls unregisterListener,
// which replaces the array being iterated. So:
//
//   - the loop walks a copy, because the live array can be replaced mid-loop;
//   - and each listener is re-checked against the current array, because a copy
//     still holds the listener that was just removed. Calling it is a state
//     update on an unmounted component.
//
// The cache entry itself can also vanish: signing out calls deleteCache, and a
// callback that triggers sign-out deletes the entry the loop is reading.
export const notifyListeners = (listeners, isRegistered, produceData) => {
    for (const listener of [...listeners]) {
        if (!isRegistered(listener)) continue;

        listener.callback(produceData(listener));
    }
}

const isStillRegistered = (key) => (listener) => {
    return has(key) && cache[key].listeners.includes(listener);
}

const updateDocument = (key, newDocRef) => {
    cache[key].value = newDocRef;
    notifyListeners(
        cache[key].listeners,
        isStillRegistered(key),
        (listener) => extractData(newDocRef, listener.parser)
    );
}

const subscribeToCollection = (key, query, listener) => {

    const unsubscribe = onSnapshot(query, (querySnapshot) => {
        updateCollection(key, querySnapshot);
    }, (error) => {
        console.error(`Cache collection update error on ${key}:\n${error}`)
        updateCollection(key, undefined);
    });

    cache[key] = {
        value: undefined,
        listeners: [listener],
        unsubscribe
    };
}

const updateCollection = (key, querySnapshot) => {
    cache[key].value = querySnapshot;
    notifyListeners(
        cache[key].listeners,
        isStillRegistered(key),
        (listener) => querySnapshot ? querySnapshot.docs.map(docSnapshot => extractData(docSnapshot, listener.parser)) : []
    );
}

const registerDocumentListener = (key, listener) => {
    cache[key].listeners.push(listener);
    const currentValue = get(key);
    const data = extractData(currentValue, listener.parser);
    listener.callback(data);
}

const registerCollectionListener = (key, listener) => {
    cache[key].listeners.push(listener);
    const currentValue = get(key);
    const collection = currentValue ? currentValue.docs.map(docSnapshot => extractData(docSnapshot, listener.parser)) : [];
    listener.callback(collection);
}

const unregisterListener = (key, removedListener) => {
    if(key && has(key))
    {
        cache[key].listeners = cache[key].listeners.filter(listener => listener !== removedListener);
    }
}

const has = (key) => {
    return Object.prototype.hasOwnProperty.call(cache, key);
}

const get = (key) => {
    if(key && has(key)){
        return cache[key].value;
    }
    return undefined;
}

const extractData = (docRef, parser) => {
    if(!docRef || !docRef.exists()){
        return undefined;
    }

     if(parser){
        return parser(docRef);
    }

    return docRef.data();
}

export const deleteCache = () => {
    Object.keys(cache).forEach(key => {
        cache[key].unsubscribe();
        delete cache[key];
    })
};

// Returns the parsed document, or undefined until the first snapshot arrives.
//
// These hooks used to take the consumer's setState and call it, which put the
// value outside React's knowledge and meant every caller carried a useState
// whose only writer was this module. Owning the state here is the ordinary hook
// contract, and it is what makes the module testable.
//
// `parser` belongs in the dependency list, so it has to be stable across
// renders. The callers pass bound methods off the FirestoreKeys singletons,
// which are; an inline arrow would resubscribe on every render.
export const useCachedDoc = (path, parser) => {
    const [value, setValue] = useState(undefined);

    useEffect(() => {

        if(!path) return;

        const listener = {callback: setValue, parser};
        if(has(path)){
            registerDocumentListener(path, listener);
        }
        else {
            subscribeToDocument(path, listener)
        }

        return () => unregisterListener(path, listener);
    }, [path, parser]);

    return value;
};

// We need a queryMemo or else this just keeps looping as the query gets regenerated on each redraw.
// There might be a better way around this but I've not thought of it.
// Returns the parsed collection. Starts as an empty array rather than
// undefined, which is what the caller's own useState did — the profile list
// distinguishes "no user yet" from "no profiles" on the user, not on this.
export const useCachedCollection = (key, queryMemo, parser) => {
    const [value, setValue] = useState([]);

    useEffect(() => {

        if(!key || !queryMemo) return;

        const listener = {callback: setValue, parser};
        if(has(key)){
            registerCollectionListener(key, listener);
        }
        else {
            subscribeToCollection(key, queryMemo, listener)
        }

        return () => unregisterListener(key, listener);
    }, [key, queryMemo, parser]);

    return value;
};